<!-- ============================================================================
     AdminActivities.vue - Version corrigée avec recherche fonctionnelle
     ============================================================================ -->
<template>
  <avs-card>
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template #title>
      <v-toolbar flat class="mb-4 bgTable">
        <!-- Recherche globale -->
        <v-text-field
          class="mr-4"
          dense
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
          @input="debouncedSync"
        />
        <!-- Date début -->
        <v-menu
          v-model="startMenu"
          transition="scale-transition"
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              hide-details
              readonly
              class="mr-4"
              :label="$t('Date début')"
              :value="startDateText"
              clearable
              @click:clear="startDate = null; onFiltersChange()"
            />
          </template>
          <v-date-picker
            v-model="startDate"
            @input="startMenu = false; onFiltersChange()"
            scrollable
          />
        </v-menu>
        <!-- Date fin -->
        <v-menu
          v-model="endMenu"
          transition="scale-transition"
          offset-y
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              hide-details
              readonly
              class="mr-4"
              :label="$t('Date fin')"
              :value="endDateText"
              clearable
              @click:clear="endDate = null; onFiltersChange()"
            />
          </template>
          <v-date-picker
            v-model="endDate"
            @input="endMenu = false; onFiltersChange()"
            scrollable
          />
        </v-menu>
        <!-- Stations -->
        <v-autocomplete
          v-model="selectedShop"
          clearable
          :items="shops"
          @input="onFiltersChange"
          item-text="name"
          item-value="id"
          :label="$t('Stations')"
          dense
          hide-details
          prepend-inner-icon="mdi-map-marker"
          class="mr-4"
        />
        <!-- Filtre > 1000 L -->
        <v-checkbox
          v-model="highQuantityOnly"
          class="mr-4 mt-0 pt-0"
          hide-details
          :label="$t('Qté > 1000 L')"
          @change="onFiltersChange"
        />
        <!-- Filtre transactions manuelles -->
        <v-checkbox
          v-model="manualOnly"
          class="mr-4 mt-0 pt-0"
          hide-details
          :label="$t('Manuelles uniquement')"
          @change="onFiltersChange"
        />
        <v-spacer />

        <!-- ====== BOUTON EXPORT EXCEL ====== -->
        <v-btn
          color="success"
          class="mr-2"
          :loading="exportingExcel"
          :disabled="exportingExcel || !items.length"
          @click="exportExcel"
        >
          <v-icon left>mdi-file-excel</v-icon>
          {{ $t('Export Excel') }}
        </v-btn>

        <!-- ====== BOUTON NOUVELLE TRANSACTION ====== -->
        <v-btn
          color="primary"
          @click="openCreateDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('Nouvelle transaction') }}
        </v-btn>
      </v-toolbar>
    </template>

    <!-- ===== CONTENU ===== -->
    <template #content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        dense
        hide-default-footer
        class="elevation-0 ma-6 bgTable"
      >
        <!-- Date -->
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>

        <!-- Utilisateur / Chauffeur + société -->
        <template #item.user="{ item }">
          <div>
            <div class="font-weight-medium">
              {{ getUserOrDriverName(item) }}
            </div>
            <div class="caption grey--text">
              {{ getUserOrVehicleContact(item) }} • {{ getComanyName(item) }}
            </div>
          </div>
        </template>

        <!-- Produit -->
        <template #item.product="{ item }">
          {{ item.product || '-' }}
        </template>

        <!-- Quantité : highlight si > 1000 L -->
        <template #item.quantity="{ item }">
          <div v-if="isHighQuantity(item)">
            <v-chip
              small
              color="error"
              text-color="white"
              class="font-weight-bold"
            >
              {{ item.quantity }} L
              <v-icon right small>mdi-alert</v-icon>
            </v-chip>
          </div>
          <div v-else>
            {{ item.quantity }} L
          </div>
        </template>

        <!-- Montant -->
        <template #item.amount="{ item }">
          {{ formatAmount(item.amount) }} XOF
        </template>

        <!-- Statut fraude / annulation -->
        <template #item.status="{ item }">
          <div class="d-flex align-center">
            <v-chip
              small
              label
              :color="statusColor(item)"
              text-color="white"
            >
              {{ statusLabel(item) }}
            </v-chip>
            <!-- Indicateurs OCR -->
            <template v-if="item.fraud_details && item.fraud_details.ocr_completed">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon
                    v-on="on"
                    small
                    class="ml-1"
                    :color="item.fraud_details.ocr_amount_match ? 'success' : 'error'"
                  >
                    {{ item.fraud_details.ocr_amount_match ? 'mdi-cash-check' : 'mdi-cash-remove' }}
                  </v-icon>
                </template>
                <span>
                  {{ item.fraud_details.ocr_amount_match ? 'Montant OCR correspond' : 'Écart montant OCR' }}
                  <span v-if="item.fraud_details.ocr_closest_amount">
                    ({{ item.fraud_details.ocr_closest_amount }} XOF, écart {{ item.fraud_details.ocr_amount_deviation_pct }}%)
                  </span>
                </span>
              </v-tooltip>
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon
                    v-on="on"
                    small
                    class="ml-1"
                    :color="item.fraud_details.ocr_product_match ? 'success' : 'warning'"
                  >
                    {{ item.fraud_details.ocr_product_match ? 'mdi-fuel' : 'mdi-alert-circle-outline' }}
                  </v-icon>
                </template>
                <span>
                  Produit : {{ item.fraud_details.ocr_product_match ? 'Trouvé sur la pompe' : 'Non détecté' }}
                </span>
              </v-tooltip>
            </template>
            <v-tooltip v-else-if="item.image && (!item.fraud_details || !item.fraud_details.ocr_completed)" bottom>
              <template #activator="{ on }">
                <v-icon v-on="on" small class="ml-1" color="grey lighten-1">mdi-text-search</v-icon>
              </template>
              <span>OCR en attente</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex align-center justify-end">
            <!-- Bouton régulariser -->
            <v-btn
              v-if="!item.has_pending_adjustment"
              small
              outlined
              color="primary"
              @click="openAdjustDialog(item)"
            >
              {{ $t('Régulariser') }}
            </v-btn>
            <v-btn
              v-else
              small
              outlined
              color="warning"
              disabled
            >
              {{ $t('En attente') }}
            </v-btn>
            <!-- Bouton pièces jointes -->
            <v-btn
              icon
              small
              class="ml-2"
              :title="$t('Voir les pièces jointes')"
              :disabled="!item.image && !item.vehicle_image"
              @click="openAttachmentsDialog(item)"
            >
              <v-icon small>mdi-paperclip</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Loader scroll infini -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate />
      </div>

      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length"
        class="text-center grey--text py-4"
      >
        {{ $t('Fin de la liste') }}
      </div>

      <!-- Sentinelle pour IntersectionObserver -->
      <div ref="sentinel" style="height:1px;"></div>

      <!-- ===================================================================
           DIALOG CRÉATION MANUELLE DE TRANSACTION
           =================================================================== -->
      <v-dialog
        v-model="createOpen"
        max-width="800"
        persistent
        scrollable
      >
        <v-card>
          <v-card-title class="text-h6 primary white--text">
            <v-icon left color="white">mdi-plus-circle</v-icon>
            {{ $t('Nouvelle transaction manuelle') }}
            <v-spacer />
            <v-btn icon dark @click="createOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-4">
            <v-form ref="createForm" v-model="createFormValid">
              <!-- ===== SECTION 1: SÉLECTION DU BÉNÉFICIAIRE ===== -->
              <div class="subtitle-1 font-weight-bold mb-2">
                <v-icon left small>mdi-account</v-icon>
                {{ $t('Bénéficiaire') }}
              </div>
              <v-divider class="mb-3" />

              <v-row dense>
                <!-- Type de bénéficiaire -->
                <v-col cols="12">
                  <v-radio-group
                    v-model="createForm.beneficiaryType"
                    row
                    dense
                    hide-details
                    class="mt-0 mb-3"
                    @change="onBeneficiaryTypeChange"
                  >
                    <v-radio
                      :label="$t('Véhicule')"
                      value="vehicle"
                    />
                    <v-radio
                      :label="$t('Utilisateur')"
                      value="user"
                    />
                  </v-radio-group>
                </v-col>

                <!-- ===== RECHERCHE VÉHICULE ===== -->
                <v-col cols="12" v-if="createForm.beneficiaryType === 'vehicle'">
                  <!-- Champ de recherche -->
                  <v-text-field
                    v-model="vehicleSearchQuery"
                    :label="$t('Rechercher un véhicule (immatriculation, chauffeur...)')"
                    dense
                    outlined
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    :loading="vehicleSearchLoading"
                    @input="onVehicleSearchInput"
                    @click:clear="clearVehicleSearch"
                    class="mb-2"
                    hint="Tapez au moins 2 caractères pour rechercher"
                    persistent-hint
                  />
                  
                  <!-- Liste des résultats -->
                  <v-card 
                    v-if="vehicleSearchResults.length > 0" 
                    outlined 
                    class="mb-3"
                    max-height="250"
                    style="overflow-y: auto;"
                  >
                    <v-list dense>
                      <v-list-item
                        v-for="vehicle in vehicleSearchResults"
                        :key="vehicle.id"
                        @click="selectVehicle(vehicle)"
                        :class="{ 'primary lighten-5': createForm.vehicle_id === vehicle.id }"
                      >
                        <v-list-item-icon>
                          <v-icon :color="createForm.vehicle_id === vehicle.id ? 'primary' : 'grey'">
                            {{ createForm.vehicle_id === vehicle.id ? 'mdi-checkbox-marked-circle' : 'mdi-car' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            {{ vehicle.registration_number }}
                            <span v-if="vehicle.driver_first_name || vehicle.driver_last_name" class="grey--text">
                              - {{ vehicle.driver_first_name }} {{ vehicle.driver_last_name }}
                            </span>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ vehicle.company_name || vehicle.company?.name || 'N/A' }}
                            <span v-if="vehicle.department_name || vehicle.department?.name">
                              • {{ vehicle.department_name || vehicle.department?.name }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-chip 
                            x-small 
                            :color="vehicle.is_blocked ? 'error' : 'success'"
                            text-color="white"
                          >
                            {{ vehicle.is_blocked ? 'Bloqué' : 'Actif' }}
                          </v-chip>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>

                  <!-- Message si pas de résultats -->
                  <v-alert
                    v-else-if="vehicleSearchQuery && vehicleSearchQuery.length >= 2 && !vehicleSearchLoading && vehicleSearchDone"
                    type="info"
                    dense
                    text
                    class="mb-3"
                  >
                    {{ $t('Aucun véhicule trouvé pour cette recherche.') }}
                  </v-alert>

                  <!-- Véhicule sélectionné -->
                  <v-alert
                    v-if="selectedVehicleData"
                    type="success"
                    dense
                    dismissible
                    class="mb-3"
                    @input="clearVehicleSelection"
                  >
                    <strong>Véhicule sélectionné :</strong> 
                    {{ selectedVehicleData.registration_number }}
                    <span v-if="selectedVehicleData.driver_first_name">
                      - {{ selectedVehicleData.driver_first_name }} {{ selectedVehicleData.driver_last_name }}
                    </span>
                    ({{ selectedVehicleData.company_name || selectedVehicleData.company?.name }})
                  </v-alert>
                </v-col>

                <!-- ===== RECHERCHE UTILISATEUR ===== -->
                <v-col cols="12" v-if="createForm.beneficiaryType === 'user'">
                  <!-- Champ de recherche -->
                  <v-text-field
                    v-model="userSearchQuery"
                    :label="$t('Rechercher un utilisateur (nom, téléphone...)')"
                    dense
                    outlined
                    clearable
                    prepend-inner-icon="mdi-magnify"
                    :loading="userSearchLoading"
                    @input="onUserSearchInput"
                    @click:clear="clearUserSearch"
                    class="mb-2"
                    hint="Tapez au moins 2 caractères pour rechercher"
                    persistent-hint
                  />
                  
                  <!-- Liste des résultats -->
                  <v-card 
                    v-if="userSearchResults.length > 0" 
                    outlined 
                    class="mb-3"
                    max-height="250"
                    style="overflow-y: auto;"
                  >
                    <v-list dense>
                      <v-list-item
                        v-for="user in userSearchResults"
                        :key="user.id"
                        @click="selectUser(user)"
                        :class="{ 'primary lighten-5': createForm.user_id === user.id }"
                      >
                        <v-list-item-icon>
                          <v-icon :color="createForm.user_id === user.id ? 'primary' : 'grey'">
                            {{ createForm.user_id === user.id ? 'mdi-checkbox-marked-circle' : 'mdi-account' }}
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium">
                            {{ user.fullname || user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username }}
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ user.phone_number || 'Pas de téléphone' }}
                            <span v-if="user.email"> • {{ user.email }}</span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card>

                  <!-- Message si pas de résultats -->
                  <v-alert
                    v-else-if="userSearchQuery && userSearchQuery.length >= 2 && !userSearchLoading && userSearchDone"
                    type="info"
                    dense
                    text
                    class="mb-3"
                  >
                    {{ $t('Aucun utilisateur trouvé pour cette recherche.') }}
                  </v-alert>

                  <!-- Utilisateur sélectionné -->
                  <v-alert
                    v-if="selectedUserData"
                    type="success"
                    dense
                    dismissible
                    class="mb-3"
                    @input="clearUserSelection"
                  >
                    <strong>Utilisateur sélectionné :</strong> 
                    {{ selectedUserData.fullname || selectedUserData.full_name || selectedUserData.username }}
                    ({{ selectedUserData.phone_number || 'N/A' }})
                  </v-alert>
                </v-col>
              </v-row>

              <!-- ===== SECTION 2: TRANSACTION ===== -->
              <div class="subtitle-1 font-weight-bold mb-2 mt-4">
                <v-icon left small>mdi-gas-station</v-icon>
                {{ $t('Détails de la transaction') }}
              </div>
              <v-divider class="mb-3" />

              <v-row dense>
                <!-- Entreprise (pour filtrer les produits) -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="createForm.company_id"
                    :items="businessCompanies"
                    :item-text="c => c.name"
                    item-value="pk"
                    :label="$t('Entreprise (fournisseur)')"
                    dense
                    outlined
                    clearable
                    :rules="[required]"
                    prepend-inner-icon="mdi-office-building"
                    @change="onCreateCompanyChange"
                  />
                </v-col>

                <!-- Produit -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="createForm.product_id"
                    :items="createProductOptions"
                    item-text="name"
                    item-value="id"
                    :label="$t('Produit')"
                    dense
                    outlined
                    clearable
                    :disabled="!createForm.company_id"
                    :rules="[required]"
                    prepend-inner-icon="mdi-fuel"
                    @change="onCreateProductChange"
                  >
                    <template #item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.price }} XOF/L</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>

                <!-- Quantité -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="createForm.quantity"
                    :label="$t('Quantité (litres)')"
                    type="number"
                    dense
                    outlined
                    :rules="[required, v => v > 0 || $t('Doit être > 0')]"
                    prepend-inner-icon="mdi-gauge"
                    suffix="L"
                    @input="onCreateQuantityInput"
                  />
                </v-col>

                <!-- Montant -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="createForm.amount"
                    :label="$t('Montant (XOF)')"
                    type="number"
                    dense
                    outlined
                    :rules="[required, v => v > 0 || $t('Doit être > 0')]"
                    prepend-inner-icon="mdi-cash"
                    suffix="XOF"
                    @input="onCreateAmountInput"
                  />
                </v-col>

                <!-- Prix unitaire (lecture seule) -->
                <v-col cols="12" md="4">
                  <v-text-field
                    :value="createSelectedProductPrice ? `${createSelectedProductPrice} XOF/L` : '-'"
                    :label="$t('Prix unitaire')"
                    dense
                    outlined
                    readonly
                    disabled
                    prepend-inner-icon="mdi-tag"
                  />
                </v-col>

                <!-- Station -->
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="createForm.shop_id"
                    :items="shops"
                    item-text="name"
                    item-value="id"
                    :label="$t('Station (optionnel)')"
                    dense
                    outlined
                    clearable
                    prepend-inner-icon="mdi-map-marker"
                  />
                </v-col>

                <!-- Kilométrage -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="createForm.kmh"
                    :label="$t('Kilométrage (optionnel)')"
                    type="number"
                    dense
                    outlined
                    clearable
                    prepend-inner-icon="mdi-speedometer"
                    suffix="km"
                  />
                </v-col>
              </v-row>

              <!-- ===== SECTION 3: DATE DE CRÉATION ===== -->
              <div class="subtitle-1 font-weight-bold mb-2 mt-4">
                <v-icon left small>mdi-calendar-clock</v-icon>
                {{ $t('Date et heure') }}
              </div>
              <v-divider class="mb-3" />

              <v-row dense>
                <!-- Choix date personnalisée ou maintenant -->
                <v-col cols="12" md="4">
                  <v-checkbox
                    v-model="createForm.useCustomDate"
                    :label="$t('Date personnalisée')"
                    hide-details
                    class="mt-0"
                  />
                </v-col>

                <!-- Date -->
                <v-col cols="12" md="4" v-if="createForm.useCustomDate">
                  <v-menu
                    v-model="createDateMenu"
                    transition="scale-transition"
                    offset-y
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-bind="attrs"
                        v-on="on"
                        :value="createForm.date"
                        :label="$t('Date')"
                        dense
                        outlined
                        readonly
                        prepend-inner-icon="mdi-calendar"
                        :rules="[v => !createForm.useCustomDate || !!v || $t('Requis')]"
                      />
                    </template>
                    <v-date-picker
                      v-model="createForm.date"
                      @input="createDateMenu = false"
                      scrollable
                    />
                  </v-menu>
                </v-col>

                <!-- Heure -->
                <v-col cols="12" md="4" v-if="createForm.useCustomDate">
                  <v-menu
                    v-model="createTimeMenu"
                    transition="scale-transition"
                    offset-y
                    :close-on-content-click="false"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-bind="attrs"
                        v-on="on"
                        :value="createForm.time"
                        :label="$t('Heure')"
                        dense
                        outlined
                        readonly
                        prepend-inner-icon="mdi-clock-outline"
                        :rules="[v => !createForm.useCustomDate || !!v || $t('Requis')]"
                      />
                    </template>
                    <v-time-picker
                      v-model="createForm.time"
                      format="24hr"
                      @click:minute="createTimeMenu = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <!-- ===== SECTION 4: PIÈCES JOINTES ===== -->
              <div class="subtitle-1 font-weight-bold mb-2 mt-4">
                <v-icon left small>mdi-paperclip</v-icon>
                {{ $t('Pièces jointes') }}
              </div>
              <v-divider class="mb-3" />

              <v-row dense>
                <!-- Image pompe -->
                <v-col cols="12" md="6">
                  <div class="d-flex flex-column">
                    <div class="caption mb-1">{{ $t('Photo pompe / ticket') }}</div>
                    <v-file-input
                      v-model="createForm.imageFile"
                      :label="$t('Sélectionner une image')"
                      accept="image/*"
                      prepend-icon=""
                      prepend-inner-icon="mdi-camera"
                      dense
                      outlined
                      show-size
                      @change="onImageFileChange('image')"
                    />
                    <v-img
                      v-if="createForm.imagePreview"
                      :src="createForm.imagePreview"
                      max-height="150"
                      contain
                      class="mt-2 rounded"
                      style="border: 1px solid #ddd;"
                    />
                  </div>
                </v-col>

                <!-- Image véhicule -->
                <v-col cols="12" md="6">
                  <div class="d-flex flex-column">
                    <div class="caption mb-1">{{ $t('Photo véhicule (optionnel)') }}</div>
                    <v-file-input
                      v-model="createForm.vehicleImageFile"
                      :label="$t('Sélectionner une image')"
                      accept="image/*"
                      prepend-icon=""
                      prepend-inner-icon="mdi-car"
                      dense
                      outlined
                      show-size
                      @change="onImageFileChange('vehicle')"
                    />
                    <v-img
                      v-if="createForm.vehicleImagePreview"
                      :src="createForm.vehicleImagePreview"
                      max-height="150"
                      contain
                      class="mt-2 rounded"
                      style="border: 1px solid #ddd;"
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- ===== SECTION 5: OPTIONS ===== -->
              <div class="subtitle-1 font-weight-bold mb-2 mt-4">
                <v-icon left small>mdi-cog</v-icon>
                {{ $t('Options') }}
              </div>
              <v-divider class="mb-3" />

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="createForm.debit_voucher"
                    :label="$t('Débiter le bon actif du véhicule')"
                    hide-details
                    class="mt-0"
                    :disabled="createForm.beneficiaryType !== 'vehicle'"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="createForm.award_points"
                    :label="$t('Attribuer des points de fidélité')"
                    hide-details
                    class="mt-0"
                  />
                </v-col>
              </v-row>

              <!-- Description -->
              <v-row dense class="mt-2">
                <v-col cols="12">
                  <v-textarea
                    v-model="createForm.description"
                    :label="$t('Description / Note (optionnel)')"
                    rows="2"
                    auto-grow
                    dense
                    outlined
                    prepend-inner-icon="mdi-note-text"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-divider />

          <v-card-actions class="pa-4">
            <v-btn text @click="createOpen = false">
              {{ $t('Annuler') }}
            </v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="createSubmitting"
              :disabled="!canSubmitCreate"
              @click="submitCreateActivity"
            >
              <v-icon left>mdi-check</v-icon>
              {{ $t('Créer la transaction') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== Dialog régularisation (existant) ===== -->
      <v-dialog v-model="adjustOpen" max-width="640">
        <v-card>
          <v-card-title class="text-h6">
            {{ $t('Demande de régularisation') }}
          </v-card-title>
          <v-card-text>
            <div class="mb-3">
              <div class="subtitle-2">
                {{ selected?.transaction_number }}
              </div>
              <div class="caption">
                {{ toDatetime(selected?.created_at) }}
              </div>
            </div>
            <v-radio-group v-model="form.action" row>
              <v-radio :label="$t('Annulation')" value="cancel" />
              <v-radio :label="$t('Modification')" value="modify" />
            </v-radio-group>
            <v-autocomplete
              v-model="selectedCompanyPk"
              :items="businessCompanies"
              :item-text="c => c.name"
              item-value="pk"
              :label="$t('Entreprise (Business)')"
              dense
              outlined
              clearable
              :disabled="form.action === 'cancel'"
              :rules="form.action === 'modify' ? [required] : []"
              @change="onCompanyChange"
              class="mb-2"
            />
            <v-textarea
              v-model="form.reason"
              :label="$t('Motif')"
              auto-grow
              dense
              rows="2"
              class="mb-2"
            />
            <div v-if="form.action === 'modify'">
              <v-autocomplete
                v-model="form.new_product"
                :items="productOptions"
                item-text="name"
                item-value="id"
                :label="$t('Nouveau produit')"
                dense
                clearable
                hide-details
                class="mb-2"
                :disabled="!selectedCompanyPk || productOptions.length === 0"
                @change="onProductChange"
              />
              <v-row class="mt-2 mb-2">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.new_quantity"
                    :label="$t('Nouvelle quantité (L)')"
                    type="number"
                    dense
                    hide-details
                    class="mt-2 mb-2"
                    @input="onLitersInput"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.new_amount"
                    :label="$t('Nouveau montant (XOF)')"
                    type="number"
                    dense
                    hide-details
                    class="mt-2 mb-2"
                    prefix="XOF "
                    @input="onAmountInput"
                  />
                </v-col>
              </v-row>
              <v-text-field
                :value="displayPrice"
                :disabled="true"
                :label="$t('Prix produit (XOF/L)')"
                dense
                outlined
                readonly
                hide-details
              />
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="adjustOpen = false">
              {{ $t('Annuler') }}
            </v-btn>
            <v-btn
              color="primary"
              :loading="submitting"
              @click="submitAdjustment"
            >
              {{ $t('Soumettre') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== Dialog pièces jointes ===== -->
      <v-dialog v-model="attachmentsOpen" max-width="900">
        <v-card>
          <v-card-title>
            <span class="text-h6">
              {{ $t('Pièces jointes') }}
              <span v-if="attachmentsSelected">
                – {{ attachmentsSelected.transaction_number }}
              </span>
            </span>
            <v-spacer />
            <v-btn icon @click="attachmentsOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <div class="subtitle-2 mb-2">
                  {{ $t('Photo pompe / ticket') }}
                </div>
                <v-img
                  v-if="attachmentsSelected && attachmentsSelected.image"
                  :src="attachmentsSelected.image"
                  max-height="320"
                  contain
                />
                <div v-else class="caption grey--text">
                  {{ $t('Aucune image principale disponible') }}
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="subtitle-2 mb-2">
                  {{ $t('Photo véhicule') }}
                </div>
                <v-img
                  v-if="attachmentsSelected && attachmentsSelected.vehicle_image"
                  :src="attachmentsSelected.vehicle_image"
                  max-height="320"
                  contain
                />
                <div v-else class="caption grey--text">
                  {{ $t('Aucune image véhicule disponible') }}
                </div>
                <v-menu v-if="attachmentsSelected && attachmentsSelected.vehicle_image" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      small
                      outlined
                      color="primary"
                      class="mt-2"
                      :loading="plateOcrTriggering"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left small>mdi-card-text-outline</v-icon>
                      {{ $t('Analyser plaque') }}
                      <v-icon right small>mdi-menu-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="triggerPlateOCR(attachmentsSelected, 'both')">
                      <v-list-item-icon><v-icon small>mdi-compare</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t('Les trois (comparaison)') }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="triggerPlateOCR(attachmentsSelected, 'google')">
                      <v-list-item-icon><v-icon small color="blue">mdi-google</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Google Vision</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="triggerPlateOCR(attachmentsSelected, 'textract')">
                      <v-list-item-icon><v-icon small color="orange">mdi-aws</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>AWS Textract</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="triggerPlateOCR(attachmentsSelected, 'azure')">
                      <v-list-item-icon><v-icon small color="blue darken-2">mdi-microsoft-azure</v-icon></v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Azure AI Vision</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </v-col>

              <!-- Résultats OCR -->
              <v-col cols="12" v-if="attachmentsSelected && attachmentsSelected.fraud_details && attachmentsSelected.fraud_details.ocr_completed">
                <v-divider class="my-3" />
                <div class="subtitle-2 mb-2">
                  <v-icon small class="mr-1">mdi-text-search</v-icon>
                  {{ $t('Analyse OCR de la photo pompe') }}
                  <v-chip v-if="attachmentsSelected.fraud_details.ocr_engine_used" x-small class="ml-2" outlined>
                    {{ attachmentsSelected.fraud_details.ocr_engine_used }}
                  </v-chip>
                </div>

                <!-- Résultat principal (montant + produit) -->
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-alert
                      dense
                      text
                      :type="attachmentsSelected.fraud_details.ocr_amount_match ? 'success' : 'error'"
                    >
                      <div class="font-weight-bold">{{ $t('Montant') }}</div>
                      <div v-if="attachmentsSelected.fraud_details.ocr_closest_amount">
                        OCR : {{ attachmentsSelected.fraud_details.ocr_closest_amount }} XOF
                        <br />
                        {{ $t('Déclaré') }} : {{ attachmentsSelected.amount }} XOF
                        <br />
                        {{ $t('Écart') }} : {{ attachmentsSelected.fraud_details.ocr_amount_deviation_pct }}%
                      </div>
                      <div v-else class="caption">{{ $t('Aucun montant détecté') }}</div>
                    </v-alert>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-alert
                      dense
                      text
                      :type="attachmentsSelected.fraud_details.ocr_product_match ? 'success' : 'warning'"
                    >
                      <div class="font-weight-bold">{{ $t('Produit') }}</div>
                      <div>
                        {{ attachmentsSelected.fraud_details.ocr_product_match ? $t('Trouvé sur la pompe') : $t('Non détecté sur la pompe') }}
                      </div>
                      <div
                        v-if="attachmentsSelected.fraud_details.ocr_product_keywords_found && attachmentsSelected.fraud_details.ocr_product_keywords_found.length"
                        class="caption"
                      >
                        {{ $t('Mots-clés') }} : {{ attachmentsSelected.fraud_details.ocr_product_keywords_found.join(', ') }}
                      </div>
                    </v-alert>
                  </v-col>
                </v-row>

                <!-- Comparaison moteurs OCR -->
                <template v-if="attachmentsSelected.fraud_details.ocr_google_raw_text || attachmentsSelected.fraud_details.ocr_textract_raw_text || attachmentsSelected.fraud_details.ocr_azure_raw_text">
                  <div class="subtitle-2 mt-3 mb-2">
                    <v-icon small class="mr-1">mdi-compare</v-icon>
                    {{ $t('Comparaison moteurs OCR') }}
                  </div>
                  <v-row dense>
                    <!-- Google Vision -->
                    <v-col cols="12" md="4">
                      <v-card outlined :class="attachmentsSelected.fraud_details.ocr_engine_used === 'google' ? 'border-success' : ''">
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="blue">mdi-google</v-icon>
                          Google Vision
                          <v-chip v-if="attachmentsSelected.fraud_details.ocr_engine_used === 'google'" x-small color="success" class="ml-2">
                            {{ $t('Utilisé') }}
                          </v-chip>
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.ocr_google_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Montant') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_google_amount_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.ocr_google_amount_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.ocr_google_closest_amount || 'N/A' }} XOF
                            ({{ attachmentsSelected.fraud_details.ocr_google_amount_deviation_pct }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Produit') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_google_product_match ? 'success' : 'warning'">
                              {{ attachmentsSelected.fraud_details.ocr_google_product_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ (attachmentsSelected.fraud_details.ocr_google_product_keywords_found || []).join(', ') || 'N/A' }}
                          </div>
                          <div class="caption">
                            <strong>Score:</strong> {{ attachmentsSelected.fraud_details.ocr_google_score }}
                          </div>
                          <div class="caption mt-1">
                            <strong>{{ $t('Montants') }}:</strong> {{ (attachmentsSelected.fraud_details.ocr_google_amounts_found || []).join(', ') }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.ocr_google_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- AWS Textract -->
                    <v-col cols="12" md="4">
                      <v-card outlined :class="attachmentsSelected.fraud_details.ocr_engine_used === 'textract' ? 'border-success' : ''">
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="orange">mdi-aws</v-icon>
                          AWS Textract
                          <v-chip v-if="attachmentsSelected.fraud_details.ocr_engine_used === 'textract'" x-small color="success" class="ml-2">
                            {{ $t('Utilisé') }}
                          </v-chip>
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.ocr_textract_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Montant') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_textract_amount_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.ocr_textract_amount_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.ocr_textract_closest_amount || 'N/A' }} XOF
                            ({{ attachmentsSelected.fraud_details.ocr_textract_amount_deviation_pct }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Produit') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_textract_product_match ? 'success' : 'warning'">
                              {{ attachmentsSelected.fraud_details.ocr_textract_product_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ (attachmentsSelected.fraud_details.ocr_textract_product_keywords_found || []).join(', ') || 'N/A' }}
                          </div>
                          <div class="caption">
                            <strong>Score:</strong> {{ attachmentsSelected.fraud_details.ocr_textract_score }}
                          </div>
                          <div class="caption mt-1">
                            <strong>{{ $t('Montants') }}:</strong> {{ (attachmentsSelected.fraud_details.ocr_textract_amounts_found || []).join(', ') }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.ocr_textract_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Azure AI Vision -->
                    <v-col cols="12" md="4">
                      <v-card outlined :class="attachmentsSelected.fraud_details.ocr_engine_used === 'azure' ? 'border-success' : ''">
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="blue darken-2">mdi-microsoft-azure</v-icon>
                          Azure AI Vision
                          <v-chip v-if="attachmentsSelected.fraud_details.ocr_engine_used === 'azure'" x-small color="success" class="ml-2">
                            {{ $t('Utilisé') }}
                          </v-chip>
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.ocr_azure_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Montant') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_azure_amount_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.ocr_azure_amount_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.ocr_azure_closest_amount || 'N/A' }} XOF
                            ({{ attachmentsSelected.fraud_details.ocr_azure_amount_deviation_pct }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Produit') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.ocr_azure_product_match ? 'success' : 'warning'">
                              {{ attachmentsSelected.fraud_details.ocr_azure_product_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ (attachmentsSelected.fraud_details.ocr_azure_product_keywords_found || []).join(', ') || 'N/A' }}
                          </div>
                          <div class="caption">
                            <strong>Score:</strong> {{ attachmentsSelected.fraud_details.ocr_azure_score }}
                          </div>
                          <div class="caption mt-1">
                            <strong>{{ $t('Montants') }}:</strong> {{ (attachmentsSelected.fraud_details.ocr_azure_amounts_found || []).join(', ') }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.ocr_azure_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-col>

              <!-- Resultats OCR plaque -->
              <v-col cols="12" v-if="attachmentsSelected && attachmentsSelected.fraud_details && attachmentsSelected.fraud_details.plate_ocr_completed">
                <v-divider class="my-3" />
                <div class="subtitle-2 mb-2">
                  <v-icon small class="mr-1">mdi-card-text-outline</v-icon>
                  {{ $t('Analyse OCR de la plaque vehicule') }}
                  <v-chip v-if="attachmentsSelected.fraud_details.plate_ocr_engine_used" x-small class="ml-2" outlined>
                    {{ attachmentsSelected.fraud_details.plate_ocr_engine_used }}
                  </v-chip>
                </div>

                <v-alert
                  dense
                  text
                  :type="attachmentsSelected.fraud_details.plate_match ? 'success' : 'error'"
                >
                  <div class="caption">
                    <strong>{{ $t('Attendu') }} :</strong>
                    {{ attachmentsSelected.fraud_details.plate_expected || '-' }}
                    <span class="grey--text">({{ attachmentsSelected.fraud_details.plate_expected_normalized || '-' }})</span>
                  </div>
                  <div class="caption">
                    <strong>{{ $t('Detecte') }} :</strong>
                    {{ attachmentsSelected.fraud_details.plate_best_candidate || '-' }}
                  </div>
                  <div class="caption">
                    <strong>{{ $t('Similarite') }} :</strong>
                    {{ Math.round((attachmentsSelected.fraud_details.plate_similarity || 0) * 100) }}%
                  </div>
                  <div class="caption" v-if="(attachmentsSelected.fraud_details.plate_candidates || []).length">
                    <strong>{{ $t('Candidats') }} :</strong>
                    {{ (attachmentsSelected.fraud_details.plate_candidates || []).join(', ') }}
                  </div>
                </v-alert>

                <!-- Comparaison par moteur (mode both) -->
                <template v-if="attachmentsSelected.fraud_details.plate_google_raw_text || attachmentsSelected.fraud_details.plate_textract_raw_text || attachmentsSelected.fraud_details.plate_azure_raw_text">
                  <div class="subtitle-2 mt-3 mb-2">
                    <v-icon small class="mr-1">mdi-compare</v-icon>
                    {{ $t('Comparaison moteurs OCR plaque') }}
                  </div>
                  <v-row dense>
                    <!-- Google -->
                    <v-col cols="12" md="4">
                      <v-card outlined>
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="blue">mdi-google</v-icon>
                          Google Vision
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.plate_google_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Match') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.plate_google_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.plate_google_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.plate_google_best_candidate || '-' }}
                            ({{ Math.round((attachmentsSelected.fraud_details.plate_google_similarity || 0) * 100) }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Candidats') }}:</strong>
                            {{ (attachmentsSelected.fraud_details.plate_google_candidates || []).join(', ') || '-' }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.plate_google_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- AWS Textract -->
                    <v-col cols="12" md="4">
                      <v-card outlined>
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="orange">mdi-aws</v-icon>
                          AWS Textract
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.plate_textract_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Match') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.plate_textract_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.plate_textract_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.plate_textract_best_candidate || '-' }}
                            ({{ Math.round((attachmentsSelected.fraud_details.plate_textract_similarity || 0) * 100) }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Candidats') }}:</strong>
                            {{ (attachmentsSelected.fraud_details.plate_textract_candidates || []).join(', ') || '-' }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.plate_textract_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Azure -->
                    <v-col cols="12" md="4">
                      <v-card outlined>
                        <v-card-title class="subtitle-2 py-2">
                          <v-icon small class="mr-1" color="blue darken-2">mdi-microsoft-azure</v-icon>
                          Azure AI Vision
                        </v-card-title>
                        <v-card-text v-if="attachmentsSelected.fraud_details.plate_azure_raw_text" class="py-1">
                          <div class="caption">
                            <strong>{{ $t('Match') }}:</strong>
                            <v-icon x-small :color="attachmentsSelected.fraud_details.plate_azure_match ? 'success' : 'error'">
                              {{ attachmentsSelected.fraud_details.plate_azure_match ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ attachmentsSelected.fraud_details.plate_azure_best_candidate || '-' }}
                            ({{ Math.round((attachmentsSelected.fraud_details.plate_azure_similarity || 0) * 100) }}%)
                          </div>
                          <div class="caption">
                            <strong>{{ $t('Candidats') }}:</strong>
                            {{ (attachmentsSelected.fraud_details.plate_azure_candidates || []).join(', ') || '-' }}
                          </div>
                        </v-card-text>
                        <v-card-text v-else class="py-1 caption grey--text">
                          {{ attachmentsSelected.fraud_details.plate_azure_error || $t('Non disponible') }}
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>

                <!-- Cas erreur OCR plaque -->
                <v-alert v-if="attachmentsSelected.fraud_details.plate_ocr_error" dense text type="warning" class="mt-2">
                  {{ attachmentsSelected.fraud_details.plate_ocr_error }}
                </v-alert>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <template v-if="attachmentsSelected && attachmentsSelected.image">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :loading="ocrTriggering"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon left small>mdi-text-search</v-icon>
                    {{ $t('Analyser OCR') }}
                    <v-icon right small>mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="triggerOCR(attachmentsSelected, 'both')">
                    <v-list-item-icon><v-icon small>mdi-compare</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ $t('Les trois (comparaison)') }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="triggerOCR(attachmentsSelected, 'google')">
                    <v-list-item-icon><v-icon small color="blue">mdi-google</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Google Vision</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="triggerOCR(attachmentsSelected, 'textract')">
                    <v-list-item-icon><v-icon small color="orange">mdi-aws</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>AWS Textract</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="triggerOCR(attachmentsSelected, 'azure')">
                    <v-list-item-icon><v-icon small color="blue darken-2">mdi-microsoft-azure</v-icon></v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Azure AI Vision</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <v-spacer />
            <v-btn text @click="attachmentsOpen = false">
              {{ $t('Fermer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { CompanyService, ShopDetailService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import debounce from "lodash/debounce"

export default {
  name: "AdminActivities",
  
  data() {
    return {
      // Filtres
      search: "",
      startDate: null,
      endDate: null,
      startMenu: false,
      endMenu: false,
      selectedShop: null,
      shops: [],
      highQuantityOnly: false,
      manualOnly: false,

      // Liste
      loading: false,
      loadingMore: false,
      page: 1,
      items: [],
      hasMore: true,
      observer: null,

      // Export
      exportingExcel: false,

      // Dialog régularisation (existant)
      adjustOpen: false,
      selected: null,
      submitting: false,

      // Dialog pièces jointes
      attachmentsOpen: false,
      attachmentsSelected: null,
      ocrTriggering: false,
      plateOcrTriggering: false,

      // Sociétés & produits
      companies: [],
      selectedCompanyPk: null,
      productOptions: [],
      selectedProductPrice: 0,
      form: {
        action: "cancel",
        reason: "",
        new_amount: null,
        new_quantity: null,
        new_product: null,
      },

      // ===== CRÉATION MANUELLE =====
      createOpen: false,
      createFormValid: false,
      createSubmitting: false,
      createDateMenu: false,
      createTimeMenu: false,
      createProductOptions: [],
      createSelectedProductPrice: 0,
      
      // Recherche véhicule
      vehicleSearchQuery: "",
      vehicleSearchResults: [],
      vehicleSearchLoading: false,
      vehicleSearchDone: false,
      selectedVehicleData: null,
      
      // Recherche utilisateur
      userSearchQuery: "",
      userSearchResults: [],
      userSearchLoading: false,
      userSearchDone: false,
      selectedUserData: null,

      createForm: {
        beneficiaryType: "vehicle",
        vehicle_id: null,
        user_id: null,
        company_id: null,
        product_id: null,
        quantity: null,
        amount: null,
        shop_id: null,
        kmh: null,
        useCustomDate: false,
        date: null,
        time: null,
        imageFile: null,
        imagePreview: null,
        imageBase64: null,
        vehicleImageFile: null,
        vehicleImagePreview: null,
        vehicleImageBase64: null,
        debit_voucher: true,
        award_points: false,
        description: "",
      },

      headers: [
        { text: this.$t("Date"), value: "created_at", width: "140" },
        { text: this.$t("Transaction #"), value: "transaction_number", width: "120" },
        { text: this.$t("Utilisateur"), value: "user" },
        { text: this.$t("Produit"), value: "product", width: "100" },
        { text: this.$t("Quantité"), value: "quantity", width: "100" },
        { text: this.$t("Montant"), value: "amount", width: "120" },
        { text: this.$t("Site"), value: "shop", width: "140" },
        { text: "", value: "actions", sortable: false, align: "end", width: "180" },
      ],
    }
  },

  created() {
    this.debouncedSync = debounce(this.onFiltersChange, 250)
    // Debounce pour la recherche
    this.debouncedVehicleSearch = debounce(this.searchVehicles, 400)
    this.debouncedUserSearch = debounce(this.searchUsers, 400)
  },

  async mounted() {
    this.applyRouteFilters()
    await Promise.all([
      this.loadCompanies(),
      this.loadShops(),
    ])
    this.fetchReset()
    this.$nextTick(this.createObserver)
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },

  computed: {
    startDateText() {
      return this.startDate ? String(this.startDate) : ""
    },
    endDateText() {
      return this.endDate ? String(this.endDate) : ""
    },
    businessCompanies() {
      return (this.companies || []).filter(
        c => String(c.company_type || "").toLowerCase() === "business"
      )
    },
    displayPrice() {
      return this.selectedProductPrice
        ? `XOF ${this.selectedProductPrice}`
        : "-"
    },
    // Vérifier si on peut soumettre le formulaire de création
    canSubmitCreate() {
      const hasBeneficiary = 
        (this.createForm.beneficiaryType === 'vehicle' && this.createForm.vehicle_id) ||
        (this.createForm.beneficiaryType === 'user' && this.createForm.user_id)
      
      const hasProduct = !!this.createForm.product_id
      const hasQuantity = this.createForm.quantity > 0
      const hasAmount = this.createForm.amount > 0
      
      return hasBeneficiary && hasProduct && hasQuantity && hasAmount && this.createFormValid
    },
  },

  methods: {
    toDatetime,

    // ===== Helpers affichage =====
    formatAmount(val) {
      if (!val) return "0"
      return Number(val).toLocaleString("fr-FR")
    },
    
    isHighQuantity(item) {
      const q = Number(item.quantity || 0)
      return q > 1000
    },
    
    getUserOrVehicleContact(item) {
      return item.vehicle
        ? item.vehicle.registration_number
        : (item.user?.phone_number || "N/A")
    },
    
    getUserOrDriverName(item) {
      if (item.vehicle && item.vehicle.driver_name) {
        return `${item.vehicle.driver_name}`
      }
      if (item.user && item.user.first_name && item.user.last_name) {
        return `${item.user.first_name} ${item.user.last_name}`
      }
      return "N/A"
    },
    
    getComanyName(item) {
      if (item.vehicle) return item.vehicle.company
      if (item.user && item.user.phone_number === "+2250000000009") {
        return item.created_by?.company?.name || ""
      }
      return ""
    },
    
    statusColor(item) {
      if (item.is_cancelled) return "error"
      const d = item.fraud_decision || "ok"
      return { ok: "success", suspect: "warning", rejected: "error" }[d] || "grey"
    },
    
    statusLabel(item) {
      if (item.is_cancelled) return this.$t("Annulée")
      const d = item.fraud_decision || "ok"
      return {
        ok: this.$t("OK"),
        suspect: this.$t("Suspect"),
        rejected: this.$t("Rejetée"),
      }[d] || d
    },
    
    async triggerOCR(item, engine = null) {
      if (!item || !item.id) return
      this.ocrTriggering = true
      try {
        await AdminServices.triggerOCR(this, item.id, engine)
        this.$store.commit("app/message", {
          msg: this.$t("Analyse OCR lancée. Rafraîchissez dans quelques secondes."),
          color: "info",
        })
        setTimeout(() => { this.fetchReset() }, 5000)
      } catch (e) {
        this.$store.commit("app/message", {
          msg: e?.response?.data?.error || this.$t("Erreur lors du lancement OCR."),
          color: "error",
        })
      } finally {
        this.ocrTriggering = false
      }
    },

    async triggerPlateOCR(item, engine = null) {
      if (!item || !item.id) return
      this.plateOcrTriggering = true
      try {
        const data = await AdminServices.triggerPlateOCR(this, item.id, engine)
        // Mise a jour reactive de fraud_details + score + decision sur l'item courant
        const fd = { ...(this.attachmentsSelected.fraud_details || {}) }
        // Purger les anciennes cles plate_* pour eviter les rendus orphelins
        // (utile quand on passe de mode "both" a single-engine : les vieux
        // plate_textract_* / plate_azure_* doivent disparaitre).
        Object.keys(fd).forEach(k => {
          if (k.startsWith("plate_")) delete fd[k]
        })
        Object.keys(data || {}).forEach(k => {
          if (k.startsWith("plate_")) fd[k] = data[k]
        })
        if (data && Array.isArray(data.rule_flags)) fd.rule_flags = data.rule_flags
        this.$set(this.attachmentsSelected, "fraud_details", fd)
        if (data && data.fraud_score !== undefined) {
          this.$set(this.attachmentsSelected, "fraud_score", data.fraud_score)
        }
        if (data && data.fraud_decision !== undefined) {
          this.$set(this.attachmentsSelected, "fraud_decision", data.fraud_decision)
        }
        this.$store.commit("app/message", {
          msg: this.$t("Analyse plaque terminee."),
          color: "success",
        })
      } catch (e) {
        this.$store.commit("app/message", {
          msg: e?.response?.data?.error || this.$t("Erreur lors de l'analyse plaque."),
          color: "error",
        })
      } finally {
        this.plateOcrTriggering = false
      }
    },

    required(v) {
      return (v !== null && v !== undefined && v !== "") || this.$t("Ce champ est requis.")
    },

    // ===== Filtres & URL =====
    applyRouteFilters() {
      const q = this.$route?.query || {}
      this.search = q.search || ""
      this.startDate = q.start || null
      this.endDate = q.end || null
    },
    
    syncRoute() {
      const q = {}
      if (this.search) q.search = this.search
      if (this.startDate) q.start = this.startDate
      if (this.endDate) q.end = this.endDate
      this.$router.replace({ name: "AdminActivities", query: q }).catch(() => {})
    },
    
    onFiltersChange() {
      this.syncRoute()
      this.fetchReset()
    },
    
    params() {
      const p = { page: this.page, ordering: "-created_at" }
      if (this.search) p.search = this.search
      if (this.startDate) p.startDate = this.startDate
      if (this.endDate) p.endDate = this.endDate
      if (this.selectedShop) p.shop = this.selectedShop
      if (this.highQuantityOnly) p.high_quantity = "true"
      if (this.manualOnly) p.manual_only = "true"
      return p
    },

    // ===== Fetch / scroll infini =====
    async fetch({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true
      try {
        const data = await AdminServices.getActivities(this, this.params())
        let results = []
        if (Array.isArray(data)) results = data
        else if (Array.isArray(data?.results)) results = data.results
        else if (Array.isArray(data?.results?.results)) results = data.results.results
        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        else console.error("Erreur chargement activités:", e)
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },

    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.fetch({ reset: true })
    },

    // ===== Export Excel =====
    // Recupere TOUTES les pages des transactions filtrees (memes filtres
    // que l'affichage : search, dates, shop, qty>1000, manuelles) puis
    // genere un .xlsx cote client.
    async exportExcel() {
      if (this.exportingExcel) return
      this.exportingExcel = true
      try {
        const collected = []
        let page = 1
        let safety = 0
        let next = true
        while (next && safety < 200) {
          const p = { ...this.params(), page }
          const data = await AdminServices.getActivities(this, p)
          let results = []
          if (Array.isArray(data)) results = data
          else if (Array.isArray(data?.results)) results = data.results
          else if (Array.isArray(data?.results?.results)) results = data.results.results
          collected.push(...results)
          next = !!(data && data.next)
          page += 1
          safety += 1
        }
        if (!collected.length) {
          this.$store.commit("app/message", {
            msg: this.$t("Aucune donnee a exporter"),
            color: "warning",
          })
          return
        }
        const XLSX = await import("xlsx")
        const { saveAs } = await import("file-saver")
        const header = [
          this.$t("N Transaction"),
          this.$t("Date"),
          this.$t("Manuelle"),
          this.$t("Cree par"),
          this.$t("Societe"),
          this.$t("Vehicule / Contact"),
          this.$t("Conducteur"),
          this.$t("Produit"),
          this.$t("Quantite (L)"),
          this.$t("Montant (XOF)"),
          this.$t("Station"),
          this.$t("Statut"),
        ]
        const rows = [header]
        collected.forEach(act => {
          const fd = act.fraud_details || {}
          // "Manuelle" : 3 sources de verite, dans l'ordre de fiabilite
          //  1) flag explicit dans fraud_details (transactions recentes)
          //  2) flag derive cote backend `is_manual_creation` (ajoute au
          //     serializer pour couvrir les transactions historiques dont
          //     fraud_details a ete ecrase)
          //  3) defaut "" (non manuelle)
          const isManual = fd.manual_creation === true
            || act.is_manual_creation === true
          const manualFlag = isManual ? "Oui" : ""
          // "Cree par" : prefere fraud_details.admin_username (capture
          // explicite a la creation). Fallback sur creator.full_name puis
          // creator.email puis creator.username (champ ajoute au
          // serializer, source = Activity.created_by FK).
          const creator = act.creator || null
          const adminUser = fd.admin_username
            || creator?.full_name
            || creator?.email
            || creator?.username
            || ""
          const vehicle = act.vehicle || null
          const contact = vehicle?.registration_number
            || act.user?.phone_number
            || ""
          const driver = vehicle?.driver_name
            || `${act.user?.first_name || ""} ${act.user?.last_name || ""}`.trim()
            || "N/A"
          rows.push([
            act.transaction_number || "",
            act.created_at ? toDatetime(act.created_at) : "",
            manualFlag,
            adminUser,
            act.company_name || "",
            contact,
            driver,
            act.product || "",
            Math.round(Number(act.quantity || 0) * 100) / 100,
            Math.round(Number(act.amount || 0)),
            act.shop || "",
            act.is_cancelled ? "Annule" : "Effectue",
          ])
        })
        const ws = XLSX.utils.aoa_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Transactions")
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
        const suffix = this.manualOnly ? "manuelles" : "all"
        saveAs(blob, `transactions_admin_${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`)
        this.$store.commit("app/message", {
          msg: this.$t("Export reussi"),
          color: "success",
        })
      } catch (e) {
        console.error("Erreur export transactions admin:", e)
        this.$store.commit("app/message", {
          msg: this.$t("Impossible d'exporter les donnees"),
          color: "error",
        })
      } finally {
        this.exportingExcel = false
      }
    },

    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this.loadMore()
        },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      )
      this.observer.observe(el)
    },
    
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetch()
    },

    // ===== Données annexes =====
    async loadCompanies() {
      try {
        const res = await CompanyService.getAll()
        this.companies = Array.isArray(res) ? res : (res?.results || [])
      } catch (e) {
        this.companies = []
      }
    },
    
    async loadShops() {
      try {
        const r = await ShopDetailService.getAdminprincipalAll(this)
        this.shops = Array.isArray(r) ? r : (r?.results || [])
      } catch (e) {
        this.shops = []
      }
    },

    // ===== RECHERCHE VÉHICULE =====
    onVehicleSearchInput(value) {
      this.vehicleSearchDone = false
      if (!value || value.length < 2) {
        this.vehicleSearchResults = []
        return
      }
      this.debouncedVehicleSearch(value)
    },

    async searchVehicles(query) {
      if (!query || query.length < 2) {
        this.vehicleSearchResults = []
        return
      }
      
      this.vehicleSearchLoading = true
      this.vehicleSearchDone = false
      
      try {
        // Utiliser l'API existante getVehicles avec le paramètre search
        const data = await AdminServices.getVehicles(this, { 
          search: query,
          page_size: 20 
        })
        
        let results = []
        if (Array.isArray(data)) {
          results = data
        } else if (data?.results) {
          results = data.results
        }
        
        // Mapper les résultats pour uniformiser la structure
        this.vehicleSearchResults = results.map(v => ({
          id: v.id || v.pk,
          registration_number: v.registration_number,
          driver_first_name: v.driver_first_name,
          driver_last_name: v.driver_last_name,
          driver_phone_number: v.driver_phone_number,
          company_name: v.company?.name || v.company_name,
          department_name: v.department?.name || v.department_name,
          is_blocked: v.is_blocked,
          is_activated: v.is_activated,
          make: v.make,
          model: v.model,
        }))
        
        console.log('Résultats véhicules:', this.vehicleSearchResults)
      } catch (e) {
        console.error('Erreur recherche véhicules:', e)
        this.vehicleSearchResults = []
      } finally {
        this.vehicleSearchLoading = false
        this.vehicleSearchDone = true
      }
    },

    selectVehicle(vehicle) {
      this.createForm.vehicle_id = vehicle.id
      this.selectedVehicleData = vehicle
      // Ne pas effacer les résultats pour permettre de changer de sélection
    },

    clearVehicleSearch() {
      this.vehicleSearchQuery = ""
      this.vehicleSearchResults = []
      this.vehicleSearchDone = false
    },

    clearVehicleSelection() {
      this.createForm.vehicle_id = null
      this.selectedVehicleData = null
    },

    // ===== RECHERCHE UTILISATEUR =====
    onUserSearchInput(value) {
      this.userSearchDone = false
      if (!value || value.length < 2) {
        this.userSearchResults = []
        return
      }
      this.debouncedUserSearch(value)
    },

    async searchUsers(query) {
      if (!query || query.length < 2) {
        this.userSearchResults = []
        return
      }
      
      this.userSearchLoading = true
      this.userSearchDone = false
      
      try {
        // Utiliser l'API existante getUsers
        const data = await AdminServices.getUsers(this, { 
          search: query,
          page_size: 20 
        })
        
        let results = []
        if (Array.isArray(data)) {
          results = data
        } else if (data?.results) {
          results = data.results
        }
        
        this.userSearchResults = results.map(u => ({
          id: u.id || u.pk,
          username: u.username,
          fullname: u.fullname || u.full_name,
          first_name: u.first_name,
          last_name: u.last_name,
          phone_number: u.phone_number,
          email: u.email,
        }))
        
        console.log('Résultats utilisateurs:', this.userSearchResults)
      } catch (e) {
        console.error('Erreur recherche utilisateurs:', e)
        this.userSearchResults = []
      } finally {
        this.userSearchLoading = false
        this.userSearchDone = true
      }
    },

    selectUser(user) {
      this.createForm.user_id = user.id
      this.selectedUserData = user
    },

    clearUserSearch() {
      this.userSearchQuery = ""
      this.userSearchResults = []
      this.userSearchDone = false
    },

    clearUserSelection() {
      this.createForm.user_id = null
      this.selectedUserData = null
    },

    // ===== CRÉATION MANUELLE - Méthodes =====
    onBeneficiaryTypeChange() {
      // Reset les sélections quand on change de type
      this.createForm.vehicle_id = null
      this.createForm.user_id = null
      this.selectedVehicleData = null
      this.selectedUserData = null
      this.vehicleSearchQuery = ""
      this.userSearchQuery = ""
      this.vehicleSearchResults = []
      this.userSearchResults = []
      
      // Désactiver le débit de bon si c'est un utilisateur
      if (this.createForm.beneficiaryType === 'user') {
        this.createForm.debit_voucher = false
      }
    },

    openCreateDialog() {
      // Reset du formulaire
      this.createForm = {
        beneficiaryType: "vehicle",
        vehicle_id: null,
        user_id: null,
        company_id: null,
        product_id: null,
        quantity: null,
        amount: null,
        shop_id: null,
        kmh: null,
        useCustomDate: false,
        date: new Date().toISOString().substr(0, 10),
        time: new Date().toTimeString().substr(0, 5),
        imageFile: null,
        imagePreview: null,
        imageBase64: null,
        vehicleImageFile: null,
        vehicleImagePreview: null,
        vehicleImageBase64: null,
        debit_voucher: true,
        award_points: false,
        description: "",
      }
      this.createProductOptions = []
      this.createSelectedProductPrice = 0
      this.vehicleSearchQuery = ""
      this.vehicleSearchResults = []
      this.vehicleSearchDone = false
      this.selectedVehicleData = null
      this.userSearchQuery = ""
      this.userSearchResults = []
      this.userSearchDone = false
      this.selectedUserData = null
      this.createOpen = true
    },

    onCreateCompanyChange(pk) {
      const c = (this.companies || []).find(x => x.pk === pk || x.id === pk)
      const products = c?.products || c?.products_company || []
      this.createProductOptions = products.map(p => ({
        id: p.id,
        name: p.name,
        price: p.price,
      }))
      this.createForm.product_id = null
      this.createSelectedProductPrice = 0
    },

    onCreateProductChange(productId) {
      const p = this.createProductOptions.find(x => x.id === productId)
      this.createSelectedProductPrice = p ? Number(p.price || 0) : 0
      if (this.createForm.quantity && this.createSelectedProductPrice) {
        this.createForm.amount = Number(
          (Number(this.createForm.quantity) * this.createSelectedProductPrice).toFixed(2)
        )
      }
    },

    onCreateQuantityInput() {
      if (!this.createSelectedProductPrice) return
      const L = Number(this.createForm.quantity || 0)
      this.createForm.amount = Number((L * this.createSelectedProductPrice).toFixed(2))
    },

    onCreateAmountInput() {
      if (!this.createSelectedProductPrice) return
      const A = Number(this.createForm.amount || 0)
      this.createForm.quantity = Number((A / this.createSelectedProductPrice).toFixed(2))
    },

    onImageFileChange(type) {
      const file = type === "image" 
        ? this.createForm.imageFile 
        : this.createForm.vehicleImageFile
      
      if (!file) {
        if (type === "image") {
          this.createForm.imagePreview = null
          this.createForm.imageBase64 = null
        } else {
          this.createForm.vehicleImagePreview = null
          this.createForm.vehicleImageBase64 = null
        }
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        if (type === "image") {
          this.createForm.imagePreview = e.target.result
          this.createForm.imageBase64 = e.target.result
        } else {
          this.createForm.vehicleImagePreview = e.target.result
          this.createForm.vehicleImageBase64 = e.target.result
        }
      }
      reader.readAsDataURL(file)
    },

    async submitCreateActivity() {
      // Vérifications
      if (this.createForm.beneficiaryType === "vehicle" && !this.createForm.vehicle_id) {
        this.$store.commit("app/message", {
          msg: this.$t("Veuillez sélectionner un véhicule."),
          color: "error",
        })
        return
      }
      if (this.createForm.beneficiaryType === "user" && !this.createForm.user_id) {
        this.$store.commit("app/message", {
          msg: this.$t("Veuillez sélectionner un utilisateur."),
          color: "error",
        })
        return
      }

      this.createSubmitting = true

      try {
        let created_at = null
        if (this.createForm.useCustomDate && this.createForm.date && this.createForm.time) {
          created_at = `${this.createForm.date}T${this.createForm.time}:00`
        }

        const payload = {
          product_id: this.createForm.product_id,
          quantity: Number(this.createForm.quantity),
          amount: Number(this.createForm.amount),
          debit_voucher: this.createForm.debit_voucher,
          award_points: this.createForm.award_points,
        }

        if (this.createForm.beneficiaryType === "vehicle") {
          payload.vehicle_id = this.createForm.vehicle_id
        } else {
          payload.user_id = this.createForm.user_id
        }

        if (this.createForm.shop_id) {
          payload.shop_id = this.createForm.shop_id
        }
        if (this.createForm.kmh) {
          payload.kmh = Number(this.createForm.kmh)
        }
        if (this.createForm.description) {
          payload.description = this.createForm.description
        }
        if (created_at) {
          payload.created_at = created_at
        }
        if (this.createForm.imageBase64) {
          payload.image = this.createForm.imageBase64
        }
        if (this.createForm.vehicleImageBase64) {
          payload.vehicle_image = this.createForm.vehicleImageBase64
        }

        console.log('Payload création:', payload)

        await AdminServices.createActivityManual(this, payload)

        this.$store.commit("app/message", {
          msg: this.$t("Transaction créée avec succès."),
          color: "success",
        })

        this.createOpen = false
        this.fetchReset()

      } catch (e) {
        console.error('Erreur création:', e)
        const errorMsg = e?.response?.data?.error 
          || e?.response?.data?.detail 
          || e?.response?.data?.message
          || (typeof e?.response?.data === 'string' ? e.response.data : null)
          || e?.message 
          || this.$t("Erreur lors de la création.")
        this.$store.commit("app/message", {
          msg: errorMsg,
          color: "error",
        })
      } finally {
        this.createSubmitting = false
      }
    },

    // ===== Régularisation (existant) =====
    onCompanyChange(pk) {
      const c = (this.companies || []).find(x => x.pk === pk || x.id === pk)
      const products = c?.products || c?.products_company || []
      this.productOptions = products.map(p => ({
        id: p.id,
        name: p.name,
        price: p.price,
      }))
      this.form.new_product = null
      this.selectedProductPrice = 0
    },
    
    onProductChange(productId) {
      const p = this.productOptions.find(x => x.id === productId)
      this.selectedProductPrice = p ? Number(p.price || 0) : 0
      if (this.form.new_quantity && this.selectedProductPrice) {
        this.form.new_amount = Number(
          (Number(this.form.new_quantity || 0) * this.selectedProductPrice).toFixed(2)
        )
      }
    },
    
    onLitersInput() {
      if (!this.selectedProductPrice) return
      const L = Number(this.form.new_quantity || 0)
      this.form.new_amount = Number((L * this.selectedProductPrice).toFixed(2))
    },
    
    onAmountInput() {
      if (!this.selectedProductPrice) return
      const A = Number(this.form.new_amount || 0)
      this.form.new_quantity = Number((A / this.selectedProductPrice).toFixed(2))
    },
    
    openAdjustDialog(item) {
      this.selected = JSON.parse(JSON.stringify(item || {}))
      this.form = {
        action: "cancel",
        reason: "",
        new_amount: null,
        new_quantity: null,
        new_product: null,
      }
      this.selectedCompanyPk = null
      this.productOptions = []
      this.selectedProductPrice = 0
      this.adjustOpen = true
    },
    
    async submitAdjustment() {
      if (!this.selected) return
      if (this.form.action === "modify") {
        if (!this.selectedCompanyPk) {
          this.$store.commit("app/message", {
            msg: this.$t("Veuillez choisir une entreprise."),
            color: "error",
          })
          return
        }
        if (!this.form.new_product) {
          this.$store.commit("app/message", {
            msg: this.$t("Veuillez choisir un produit."),
            color: "error",
          })
          return
        }
      }
      this.submitting = true
      try {
        const payload = {
          activity: this.selected.id,
          action: this.form.action,
          reason: this.form.reason || "",
        }
        if (this.form.action === "modify") {
          if (this.form.new_amount != null) {
            payload.new_amount = Number(this.form.new_amount)
          }
          if (this.form.new_quantity != null) {
            payload.new_quantity = Number(this.form.new_quantity)
          }
          if (this.form.new_product) {
            payload.new_product = this.form.new_product
          }
        }
        await AdminServices.createActivityAdjustment(this, payload)
        this.$store.commit("app/message", {
          msg: this.$t("Demande soumise."),
          color: "success",
        })
        this.adjustOpen = false
        this.fetchReset()
      } catch (e) {
        this.$store.commit("app/message", {
          msg: this.$t("Échec de la soumission."),
          color: "error",
        })
      } finally {
        this.submitting = false
      }
    },

    // ===== Pièces jointes =====
    openAttachmentsDialog(item) {
      this.attachmentsSelected = item
      this.attachmentsOpen = true
    },
  },
}
</script>

<style scoped>
.v-card__title.primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}
</style>